import { useEffect, useRef, useState } from 'react';

import { Button, XStack, YStack } from '@/components/ui';
import { FeedTrayCta } from '@/features/action-cards-view/feed';
import { startChatWithPrompt } from '@/features/chat/start-chat-with-prompt';
import type { IntegrationSuggestion } from '@/features/integrations/integrations.types';
import type {
  SuggestionChip,
  SuggestionFeedbackAction,
} from '@/features/integrations/suggestion-chip.types';
import { trackEvent } from '@/utils/analytics';

import { ConnectedBadge, SuggestionCard } from './suggestion-card';
import { suggestionExperimentProperties } from './suggestion-experiment';
import { useSuggestionShown } from './use-integration-suggestions';

type ConfiguredSuggestion = IntegrationSuggestion & { chip: SuggestionChip };
type Props = {
  suggestion: ConfiguredSuggestion;
  position: number;
  connect: (suggestion: IntegrationSuggestion) => Promise<void>;
  dismiss: (suggestion: IntegrationSuggestion) => void;
};

export const ConfigurableSuggestionRow = ({
  suggestion: incoming,
  position,
  connect,
  dismiss,
}: Props) => {
  // Keep the displayed copy, action arguments and attribution together until this row unmounts.
  const [suggestion] = useState(incoming);
  const { chip } = suggestion;
  const secondary = chip.secondary;
  const dismissSecondary = secondary?.kind === 'dismiss' ? secondary : null;
  const feedbackClose =
    secondary?.kind === 'feedback' && secondary.trigger === 'close'
      ? secondary
      : null;
  const cardDismiss = dismissSecondary ?? feedbackClose;
  const [feedback, setFeedback] = useState<SuggestionFeedbackAction | null>(
    null
  );
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const feedbackRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (feedback) feedbackRef.current?.focus();
  }, [feedback]);
  const shownRef = useSuggestionShown(suggestion, position, 'home');
  const properties = {
    ...suggestionExperimentProperties(suggestion, 'home'),
    integration: suggestion.toolkit_slug,
    source: suggestion.source,
    action: suggestion.action,
    surface: 'home',
  };

  const runAction = async (
    action: SuggestionChip['primary'] | NonNullable<SuggestionChip['secondary']>
  ) => {
    trackEvent('HOME_INTEGRATION_SUGGESTION_CTA_CLICKED', {
      ...properties,
      cta_id: action.id,
      cta_kind: action.kind,
    });
    switch (action.kind) {
      case 'connect':
        setBusy(true);
        try {
          await connect(suggestion);
        } finally {
          setBusy(false);
        }
        break;
      case 'start_chat':
        trackEvent('HOME_INTEGRATION_SUGGESTION_TRY_CLICKED', properties);
        startChatWithPrompt(action.prompt);
        dismiss(suggestion);
        break;
      case 'dismiss':
        dismiss(suggestion);
        break;
      case 'feedback':
        setFeedback(action);
        trackEvent('HOME_INTEGRATION_SUGGESTION_FEEDBACK_SHOWN', {
          ...properties,
          cta_id: action.id,
        });
        break;
    }
  };

  const finishFeedback = (optionId: string | null) => {
    if (!feedback) return;
    trackEvent(
      optionId === null
        ? 'HOME_INTEGRATION_SUGGESTION_FEEDBACK_SKIPPED'
        : 'HOME_INTEGRATION_SUGGESTION_FEEDBACK_SUBMITTED',
      {
        ...properties,
        cta_id: feedback.id,
        option_id: optionId,
      }
    );
    if (feedback.dismiss_after) dismiss(suggestion);
    else {
      setConfirmation(optionId === null ? null : feedback.confirmation);
      setFeedback(null);
    }
  };

  return (
    <SuggestionCard
      suggestion={suggestion}
      shownRef={shownRef}
      dismiss={
        cardDismiss && !feedback && !confirmation
          ? {
              label: `${cardDismiss.label} — ${suggestion.display_name}`,
              onClick: () => void runAction(cardDismiss),
              disabled: busy,
            }
          : undefined
      }
      badge={
        suggestion.action === 'try' ? (
          <ConnectedBadge className="-bottom-1 -right-1 size-4" />
        ) : null
      }
      title={
        suggestion.action === 'try'
          ? `${suggestion.display_name} is connected`
          : suggestion.display_name
      }
      message={chip.message}
      actions={
        <YStack className="ac-scale gap-2">
          {feedback ? (
            <YStack
              ref={feedbackRef}
              tabIndex={-1}
              role="group"
              aria-label={feedback.question}
              className="gap-2 border-t border-border pt-3 outline-none"
            >
              <span className="break-words text-sm" aria-live="polite">
                {feedback.question}
              </span>
              <XStack className="flex flex-wrap gap-2">
                {feedback.options.map((option) => (
                  <Button
                    key={option.id}
                    variant="secondary"
                    size="sm"
                    className="h-auto min-h-8 whitespace-normal break-words"
                    onClick={() => finishFeedback(option.id)}
                  >
                    {option.label}
                  </Button>
                ))}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto min-h-8 whitespace-normal break-words"
                  onClick={() => finishFeedback(null)}
                >
                  {feedback.skip_label}
                </Button>
              </XStack>
            </YStack>
          ) : (
            <>
              {confirmation && (
                <span
                  role="status"
                  className="break-words text-xs text-foreground-muted"
                >
                  {confirmation}
                </span>
              )}
              {secondary && secondary.kind !== 'dismiss' && !feedbackClose && (
                <Button
                  variant="ghost"
                  size="xs"
                  className="h-auto min-h-[calc(28*var(--ac-u))] max-w-full self-start whitespace-normal break-words rounded-full px-[calc(9*var(--ac-u))] py-1 text-left text-[length:calc(12.5*var(--ac-u))] leading-4 text-foreground-muted hover:text-foreground"
                  disabled={busy}
                  onClick={() => void runAction(secondary)}
                >
                  {secondary.label}
                </Button>
              )}
            </>
          )}
          <FeedTrayCta
            label={chip.primary.label}
            accessibleLabel={`${chip.primary.label} — ${suggestion.display_name}`}
            className="w-full min-w-0 h-auto min-h-8 max-w-full justify-center whitespace-normal break-words px-3 py-1 text-center leading-4"
            disabled={busy}
            onClick={() => void runAction(chip.primary)}
          />
        </YStack>
      }
    />
  );
};
